import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Award, Users, CheckCircle, Filter, X, Bed, Calendar, Star, Building } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import { useTranslation } from '../hooks/useTranslation';
import { Skeleton } from '../components/ui/skeleton';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function Hospitals() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    specialty: '',
    accreditation: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    states: [],
    specialties: [],
    accreditations: []
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/public/hospitals`);
      if (response.ok) {
        const data = await response.json();
        setHospitals(data.hospitals || []);
        
        // Extract unique filter options
        const allHospitals = data.hospitals || [];
        setFilterOptions({
          cities: [...new Set(allHospitals.map(h => h.city).filter(Boolean))].sort(),
          states: [...new Set(allHospitals.map(h => h.state).filter(Boolean))].sort(),
          specialties: [...new Set(allHospitals.flatMap(h => h.specialties || []))].sort(),
          accreditations: [...new Set(allHospitals.map(h => h.accreditation).filter(Boolean))].sort()
        });
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  // Convert hospital name to URL-safe slug
  const getHospitalSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  // Filter hospitals based on selected filters
  const filteredHospitals = hospitals.filter(hospital => {
    if (filters.city && hospital.city !== filters.city) return false;
    if (filters.state && hospital.state !== filters.state) return false;
    if (filters.specialty && !(hospital.specialties || []).includes(filters.specialty)) return false;
    if (filters.accreditation && hospital.accreditation !== filters.accreditation) return false;
    return true;
  });

  const clearFilters = () => {
    setFilters({ city: '', state: '', specialty: '', accreditation: '' });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  // Loading skeleton
  const HospitalSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-start gap-4">
        <Skeleton className="w-16 h-16 rounded-xl" />
        <div className="flex-1">
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-20" />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(193_95%_95%)] to-[hsl(210_40%_98%)]">
      <PublicHeader currentPage="hospitals" />
      
      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[hsl(var(--foreground))] mb-4" data-testid="hospitals-title">
              {t('hospitals.heroTitle')} <span className="text-[hsl(var(--primary))]">{t('hospitals.heroTitleHighlight')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              {t('hospitals.heroSubtitle')}
            </p>
          </div>

          {/* Filter Toggle Button (Mobile) */}
          <div className="md:hidden mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="bg-[hsl(var(--primary))] text-white text-xs px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>

          {/* Filters Section */}
          <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 mb-8 ${showFilters ? 'block' : 'hidden md:block'}`}>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <select
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-city"
                >
                  <option value="">All Cities</option>
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-state"
                >
                  <option value="">All States</option>
                  {filterOptions.states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
                <select
                  value={filters.specialty}
                  onChange={(e) => setFilters({ ...filters, specialty: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-specialty"
                >
                  <option value="">All Specialties</option>
                  {filterOptions.specialties.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Accreditation</label>
                <select
                  value={filters.accreditation}
                  onChange={(e) => setFilters({ ...filters, accreditation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-accreditation"
                >
                  <option value="">All Accreditations</option>
                  {filterOptions.accreditations.map(acc => (
                    <option key={acc} value={acc}>{acc}</option>
                  ))}
                </select>
              </div>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg self-end"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredHospitals.length}</span> hospitals
            </p>
          </div>

          {/* Hospitals Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => <HospitalSkeleton key={i} />)}
            </div>
          ) : filteredHospitals.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No hospitals found</h3>
              <p className="text-gray-500">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredHospitals.map((hospital, index) => (
                <div
                  key={hospital._id || index}
                  onClick={() => navigate(`/hospitals/${hospital.user_id || getHospitalSlug(hospital.name)}`)}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`hospital-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(193_80%_50%)] rounded-xl flex items-center justify-center text-white text-2xl shadow-lg">
                      <Building className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-[hsl(var(--primary))] transition-colors">
                        {hospital.name}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-600 mt-1">
                        <MapPin className="w-4 h-4" />
                        <span>{hospital.city}, {hospital.state}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Award className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-gray-600">{hospital.accreditation}</span>
                      </div>
                      {hospital.rating > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm font-medium">{hospital.rating.toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Specialties */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(hospital.specialties || []).slice(0, 4).map((spec, idx) => (
                      <span key={idx} className="px-2 py-1 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-xs rounded-full">
                        {spec}
                      </span>
                    ))}
                    {(hospital.specialties || []).length > 4 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        +{hospital.specialties.length - 4} more
                      </span>
                    )}
                  </div>
                  
                  {/* Stats */}
                  <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-600">
                        <Bed className="w-4 h-4" />
                        <span className="font-semibold">{hospital.beds || 'N/A'}+</span>
                      </div>
                      <span className="text-xs text-gray-500">Beds</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span className="font-semibold">{hospital.doctors || 'N/A'}+</span>
                      </div>
                      <span className="text-xs text-gray-500">Doctors</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span className="font-semibold">{hospital.established || 'N/A'}</span>
                      </div>
                      <span className="text-xs text-gray-500">Est.</span>
                    </div>
                  </div>
                  
                  {/* Services Tags */}
                  {(hospital.visa_assistance || hospital.airport_pickup) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {hospital.visa_assistance && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Visa Assistance
                        </span>
                      )}
                      {hospital.airport_pickup && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Airport Pickup
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
